import { graphql } from 'gatsby'
import React from 'react'
import CountdownBanner, {
  CountdownBannerContent,
} from 'src/components/organisms/ctas/countdownBanner'
import { PrismicPageDataBodyCountdownBanner } from 'src/typings/generated/graphql'

/**
 * Maps an ISO string to a `Date` instance. If the ISO string is invalid, it will return `undefined`.
 *
 * @param isoString The ISO string from the CMS to map into a `Date` instance.
 */
const mapIsoStringToDate = (
  isoString: string | null | undefined
): Date | undefined => {
  if (!isoString) {
    return undefined
  }

  const date = new Date(isoString)
  return Number.isNaN(date.getTime()) ? undefined : date
}

/**
 * Clamps a number to a range defined by a minimum and maximum value, replacing the value with the nearest boundary if
 * it falls outside of the range.
 *
 * @param min the minimum value in the acceptable range, replaces the value if it falls below it.
 * @param value the value to clamp to a range.
 * @param max the maximum value in the acceptable range, replaces the value if it exceeds it.
 */
const clamp = (min: number, value: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/**
 * Props for the `BannerCtaSlice` component.
 */
type BannerCtaSliceProps = {
  slice: PrismicPageDataBodyCountdownBanner
}

/**
 * Maps a Prismic slice of type `PrismicPageDataBodyCountdownBanner` to the props required by the `CountdownBanner`
 * component and renders it.
 */
const BannerCtaSlice = ({ slice }: BannerCtaSliceProps) => {
  const content: CountdownBannerContent = {
    anchor: slice.primary.anchor ?? undefined,
    eventDate: mapIsoStringToDate(slice.primary.event_date),
    headlineBeforeEvent: slice.primary.headline_before_event ?? undefined,
    backgroundImage: {
      imageData: slice.primary.background_image?.gatsbyImageData ?? undefined,
      alt: slice.primary.background_image?.alt ?? undefined,
      focusX: slice.primary.background_image_focus_x
        ? clamp(0, slice.primary.background_image_focus_x, 1)
        : undefined,
      focusY: slice.primary.background_image_focus_y
        ? clamp(0, slice.primary.background_image_focus_y, 1)
        : undefined,
    },
    headlineAfterEvent: slice.primary.heading_after_event ?? undefined,
    description: slice.primary.description_text ?? undefined,
    ctaLink: slice.primary.cta_link ?? undefined,
    ctaText: slice.primary.cta_text ?? undefined,
    ctaIcon: {
      imageData: slice.primary.cta_icon?.gatsbyImageData ?? undefined,
      alt: slice.primary.cta_icon?.alt ?? undefined,
    },
  }

  return <CountdownBanner content={content} />
}

export default BannerCtaSlice

export const CountdownBannerFragment = graphql`
  fragment PageDataBodyCountdownBanner on PrismicPageDataBodyCountdownBanner {
    primary {
      anchor
      event_date
      headline_before_event
      background_image {
        gatsbyImageData
        alt
      }
      background_image_focus_x
      background_image_focus_y
      heading_after_event
      description_text
      cta_link {
        ...BasicLinkFragment
      }
      cta_text
      cta_icon {
        gatsbyImageData
        alt
      }
    }
  }
`
